/**
 * Compares two arrays for equality.
 *
 * @param {Array} a - The first array to compare.
 * @param {Array} b - The second array to compare.
 * @returns {boolean} Returns true if the arrays are equal, false otherwise.
 */
const areArraysEqual = (a, b) => {
    if (a.length !== b.length) return false;

    return a.every((val, i) => areValuesEqual(val, b[i]));
}

/**
 * Checks if the provided value is a plain object.
 *
 * A plain object is defined as an object that is not null, 
 * is of type 'object', and is not an array.
 *
 * @param {any} obj - The value to check.
 * @returns {boolean} - Returns true if the value is a plain object, otherwise false.
 */
const isPlainObject = (obj) => {
    return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

/**
 * Compares two objects for shallow equality.
 * 
 * This function checks if both objects have the same number of keys and if all keys in the first object
 * have equal values in the second object. It uses a helper function `areValuesEqual` to compare the values.
 *
 * @param {Object} obj1 - The first object to compare.
 * @param {Object} obj2 - The second object to compare.
 * @returns {boolean} - Returns true if the objects are shallowly equal, otherwise false.
 */
const shallowObjectEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => obj2.hasOwnProperty(key) && areValuesEqual(obj1[key], obj2[key]));
};

/**
 * Compares two values for equality.
 * It checks if both values are arrays, objects, or primitive types and compares them accordingly.
 *
 * @param {*} value1 - The first value to compare.
 * @param {*} value2 - The second value to compare.
 * @returns {boolean} - Returns true if the values are equal, false otherwise.
 */
export const areValuesEqual = (value1, value2) => {
    const bothAreArrays = Array.isArray(value1) && Array.isArray(value2);
    const bothAreObjects = isPlainObject(value1) && isPlainObject(value2);
    const bothArePrimitives =
        !Array.isArray(value1) &&
        !Array.isArray(value2) &&
        !isPlainObject(value1) &&
        !isPlainObject(value2);

    if (bothAreArrays) {
        return areArraysEqual(value1, value2);
    }

    if (bothAreObjects) {
        return shallowObjectEqual(value1, value2);
    }

    if (bothArePrimitives) {
        return value1 === value2;
    }

    return false;
};